import { Calendar, User, Star, Download, Play, Settings, Edit, Copy, Trash2, Plus, Eye, Zap, Activity, Clock } from 'lucide-react'
import React from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card'
import { BlueprintWithDetails } from '../../types/blueprints'

interface BlueprintCardProps {
  blueprint: BlueprintWithDetails
  variant?: 'default' | 'marketplace'
  onEdit?: (blueprint: BlueprintWithDetails) => void
  onRun?: (blueprint: BlueprintWithDetails) => void
  onDuplicate?: (blueprint: BlueprintWithDetails) => void
  onDownload?: (blueprint: BlueprintWithDetails) => void
  onDelete: (blueprint: BlueprintWithDetails) => void
  onAddToProject?: (blueprint: BlueprintWithDetails) => void
  onSimulate?: (blueprint: BlueprintWithDetails) => void
  onViewDetails?: (blueprint: BlueprintWithDetails) => void
}

export const BlueprintCard: React.FC<BlueprintCardProps> = ({
  blueprint,
  variant = 'default',
  onEdit,
  onRun,
  onDuplicate,
  onDownload,
  onDelete,
  onAddToProject,
  onSimulate,
  onViewDetails
}) => {
  const isMarketplace = variant === 'marketplace'

  return (
    <Card className="w-full hover:shadow-lg transition-shadow duration-200">
      <CardHeader className="" hasCardAction={false}>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="text-lg font-semibold line-clamp-2">
              {blueprint.name}
            </CardTitle>
            <CardDescription className="mt-1 line-clamp-2">
              {blueprint.description}
            </CardDescription>
          </div>
          <div className="flex flex-col items-end gap-2 ml-4">
            <Badge variant={blueprint.isPublic ? 'default' : 'secondary'}>
              {blueprint.isPublic ? 'Public' : 'Private'}
            </Badge>
            {blueprint.category && (
              <Badge variant="outline" className="text-xs">
                {blueprint.category}
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent>
        <div className="space-y-4">
          {/* Stats */}
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              <span>
                {new Date(blueprint.createdAt).toLocaleDateString()}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <User className="h-4 w-4" />
              <span>v{blueprint.version}</span>
            </div>
            {isMarketplace && blueprint.marketplace && (
              <>
                <div className="flex items-center gap-1">
                  <Star className="h-4 w-4" />
                  <span>{blueprint.marketplace.rating?.toFixed(1) || 'N/A'}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Download className="h-4 w-4" />
                  <span>{blueprint.marketplace.downloadCount || 0}</span>
                </div>
              </>
            )}
          </div>

          {/* Tags */}
          {blueprint.tags && blueprint.tags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {blueprint.tags.map((tag, index) => (
                <Badge key={index} variant="outline" className="text-xs">
                  {tag}
                </Badge>
              ))}
            </div>
          )}

          {/* Node count info */}
          <div className="text-sm text-muted-foreground">
            {Array.isArray(blueprint.nodeDefinitions) 
              ? `${blueprint.nodeDefinitions.length} nodes`
              : 'No nodes defined'
            }
          </div>
        </div>
      </CardContent>      <CardFooter className="flex justify-between p-4 border-t">
        <div className="flex flex-wrap gap-2">
          {onAddToProject && (
            <Button
              variant="default"
              size="sm"
              onClick={() => onAddToProject(blueprint)}
              className="flex-shrink-0"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add to Project
            </Button>
          )}
          {!isMarketplace && onEdit && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onEdit(blueprint)}
              className="flex-shrink-0"
            >
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
          )}
          {onRun && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onRun(blueprint)}
              className="flex-shrink-0"
            >
              <Play className="h-4 w-4 mr-2" />
              Run
            </Button>
          )}
          {onSimulate && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onSimulate(blueprint)}
              className="flex-shrink-0"
            >
              <Zap className="h-4 w-4 mr-2" />
              Simulate
            </Button>
          )}
          {onViewDetails && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onViewDetails(blueprint)}
              className="flex-shrink-0"
            >
              <Eye className="h-4 w-4 mr-2" />
              Details
            </Button>
          )}
          {isMarketplace && onDownload && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onDownload(blueprint)}
              className="flex-shrink-0"
            >
              <Download className="h-4 w-4 mr-2" />
              Download
            </Button>
          )}
          {!isMarketplace && onDuplicate && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onDuplicate(blueprint)}
              className="flex-shrink-0"
            >
              <Copy className="h-4 w-4 mr-2" />
              Duplicate
            </Button>
          )}
          <Button
            variant="destructive"
            size="sm"
            onClick={() => onDelete(blueprint)}
            className="flex-shrink-0 ml-auto"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </Button>
        </div>
      </CardFooter>
    </Card>
  )
}
